import { useContext, useState, useEffect, useRef } from 'react';
import GlobalContext from "../context/GlobalContext.js";
import Footer from '../components/footer.js';
import getAPI from '../functions/API/getAPI.js';
import postAPI from '../functions/API/postAPI.js';
import HomePageWalkthrough from '../components/homepageWalkThrough.js';
import { toast } from '../lib/toast.js';
import AgreementSwitcher from '../components/AgreementSwitcher.js';
import AccountSwitcher from '../components/AccountSwitcher.js';
import { darkenColor } from '../functions/colorUtils.js';
import AllPayDirectDebitCard from '../components/AllPayDirectDebitCard.js';
import DesktopNavBar from '../components/DesktopNavBar.js';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { DocumentTextIcon, HomeIcon } from '@heroicons/react/20/solid';
import {
    Home as HomeIconLucide,
    ArrowLeftRight,
    ChevronRight,
    Settings,
    CircleUserRound,
    CreditCard,
    Landmark,
    CalendarDays,
    Hammer,
    ClipboardCheck,
    MessageSquare,
    LogOut,
    Sun,
    Moon,
    EyeOff,
    X,
} from 'lucide-react';
import moment from 'moment';

function dedupeAccounts(agr) {
    if (!agr?.accounts) return agr;
    const seen = new Set();
    const unique = [];
    for (const acc of agr.accounts) {
        const key = acc.accountid || acc.id;
        if (key && seen.has(key)) continue;
        if (key) seen.add(key);
        unique.push(acc);
    }
    return { ...agr, accounts: unique };
}

export default function Home() {
    const { unreadCount, agreementId } = useOutletContext();
    const navigate = useNavigate();
    const [agreement, setAgreement] = useState('');
    const [agreements, setAgreements] = useState('');
    const [selectedAgreement, setSelectedAgreement] = useState();
    const [modalIsOpen, setIsOpen] = useState(false);
    const [loading, isLoading] = useState(true);
    const { theme, updateTheme, themeCSS, settings, fontSize, updateFontSize } = useContext(GlobalContext);
    const [userDetails, setUserDetails] = useState();
    const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
    const headerMenuRef = useRef(null);
    const [accountModalIsOpen, setAccountModalIsOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState('');
    const [accountModalFunction, setAccountModalFunction] = useState();
    const [triggerAllPayDD, setTriggerAllPayDD] = useState();
    const [paymentModal, setPaymentModal] = useState(null); // { accountreference, token, gatewayurl, clientcode }
    const [paymentResult, setPaymentResult] = useState(null); // 'success' | 'cancelled' | 'error'
    const [paymentLoading, setPaymentLoading] = useState(false);

    // New state for redesigned sections
    const [transactions, setTransactions] = useState([]);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [openRepairs, setOpenRepairs] = useState([]);
    const [openRequests, setOpenRequests] = useState([]);
    const [activeAccountIndex, setActiveAccountIndex] = useState(0);
    const carouselRef = useRef(null);

    const primaryColor = settings?.primary_hex_colour || '#813468';
    const primaryDark = darkenColor(primaryColor);

    async function logout() {
        const body = { "": "" };
        let destroy = await postAPI('token/destroy', body);
        if (destroy.response.ok) {
            localStorage.clear();
            window.location.href = '/';
        }
    }

    // Close header menu when clicking outside
    useEffect(() => {
        function handleClickOutside(e) {
            if (headerMenuRef.current && !headerMenuRef.current.contains(e.target)) {
                setHeaderMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    function switchAccount(agreementreference) {
        isLoading(true);
        setSelectedAgreement(agreementreference);
        localStorage.setItem('agreement', agreementreference);
        setIsOpen(false);
    }

    async function makeAllPayPayment(accountreference) {
        setPaymentLoading(true);
        const body = { reference: agreement.reference };
        let PaymentProviderDetail = await postAPI('makepayment', body);
        let response = PaymentProviderDetail.response;
        if (response.ok) {
            const params = {
                accountreference,
                token: PaymentProviderDetail.data.Token,
                gatewayurl: PaymentProviderDetail.data.gatewayurl || settings.allpay_gatewayurl,
                clientcode: PaymentProviderDetail.data.ClientCode,
            };
            setPaymentResult(null);
            setPaymentModal(params);
            // Submit the AllPay form into the iframe after it mounts
            setTimeout(() => {
                const form = document.createElement('form');
                form.method = 'post';
                form.action = params.gatewayurl;
                form.target = 'PaymentModalIFrame';
                const fields = {
                    token: params.token,
                    customerReference: params.accountreference,
                    amount: 0,
                    clientCode: params.clientcode,
                    paymentReason: 'Portal Payment',
                };
                Object.entries(fields).forEach(([k, v]) => {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = k;
                    input.value = v;
                    form.appendChild(input);
                });
                document.body.appendChild(form);
                form.submit();
                document.body.removeChild(form);
            }, 100);
        } else {
            toast.error('There was an error connecting to the payment service. Please try again later');
        }
        setPaymentLoading(false);
    }

    // Listen for AllPay payment completion messages
    useEffect(() => {
        function handlePaymentMessage(event) {
            if (!paymentModal) return;
            if (event.data.resultCode !== undefined) {
                if (event.data.resultCode === 0) {
                    setPaymentResult('success');
                } else if (event.data.resultCode < 0) {
                    setPaymentResult('cancelled');
                } else {
                    setPaymentResult('error');
                }
            }
        }
        window.addEventListener('message', handlePaymentMessage);
        return () => window.removeEventListener('message', handlePaymentMessage);
    }, [paymentModal]);

    function closePaymentModal() {
        setPaymentModal(null);
        setPaymentResult(null);
    }

    async function GetAPIData() {
        const AgreementsResponse = await getAPI('agreements');
        if (AgreementsResponse.response.ok) {
            const agreementreference = localStorage.getItem('agreement');
            if (!agreementreference) {
                let array = AgreementsResponse.data;
                let finalAgreements = [];
                array.filter(item => item.type === "Residential").forEach(element => { finalAgreements.push(element); });
                array.filter(item => item.type !== "Residential").forEach(element => { finalAgreements.push(element); });
                const firstAgr = dedupeAccounts(finalAgreements[0]);
                setAgreement(firstAgr);
                localStorage.setItem('agreement', firstAgr.reference);
                setSelectedAccount(firstAgr.accounts[0]);
            } else {
                const AgreementResponse = await getAPI('agreements/' + agreementreference);
                if (AgreementResponse.response.ok) {
                    const agr = dedupeAccounts(AgreementResponse.data);
                    setAgreement(agr);
                    setSelectedAccount(agr.accounts[0]);
                } else {
                    localStorage.removeItem('agreement');
                    GetAPIData();
                }
            }
            let index = AgreementsResponse.data.findIndex(obj => obj.type === 'Residential');
            if (index !== -1) {
                let removedItem = AgreementsResponse.data.splice(index, 1)[0];
                AgreementsResponse.data.unshift(removedItem);
            }
            setAgreements(AgreementsResponse.data);
        }
        isLoading(false);
    }

    async function GetUserDetails() {
        const UserDetailsResponse = await getAPI('userdetails/summary');
        if (UserDetailsResponse.response.ok) {
            setUserDetails(UserDetailsResponse.data);
        }
    }

    async function getTransactionsForAccount(accountId) {
        if (!accountId) return;
        setTransactionsLoading(true);
        const startDate = moment().subtract(3, 'months').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');
        const url = `transactions/${accountId}/?startdate=${startDate}&enddate=${endDate}`;
        const response = await getAPI(url);
        if (response.response && response.response.ok) {
            setTransactions(response.data || []);
        } else {
            setTransactions([]);
        }
        setTransactionsLoading(false);
    }

    async function getAppointments() {
        try {
            const response = await getAPI('repairsappointments');
            if (response.response?.ok && response.data && response.data.length > 0) {
                let filtered = response.data.filter(apt => {
                    let today = moment().format('YYYY-MM-DD');
                    return moment(apt.appointmentdate).format('YYYY-MM-DD') >= today && !apt.is_complete;
                }).sort((a, b) => new Date(a.appointmentdate) - new Date(b.appointmentdate));
                setAppointments(filtered);
            }
        } catch (e) { }
    }

    async function getOpenRepairs() {
        if (!agreement?.assetid) return;
        try {
            const response = await getAPI('repairs/' + agreement.assetid);
            if (response.response?.ok && response.data) {
                setOpenRepairs(response.data.filter(r => !r.iscomplete));
            }
        } catch (e) { }
    }

    async function getOpenRequests() {
        if (!settings?.requests_enabled) return;
        try {
            const response = await getAPI('taskinstances');
            if (response.response?.ok && response.data?.task_instances) {
                setOpenRequests(
                    response.data.task_instances.filter(i => i.status !== 'COMPLETED' && i.status !== 'CANCELLED')
                );
            }
        } catch (e) { }
    }

    // Fetch secondary data once agreement is loaded
    useEffect(() => {
        if (agreement && agreement.accounts) {
            const firstAccount = agreement.accounts[activeAccountIndex] || agreement.accounts[0];
            if (firstAccount?.accountid) {
                getTransactionsForAccount(firstAccount.accountid);
            }
            getOpenRepairs();
        }
    }, [agreement]);

    useEffect(() => {
        if (settings) {
            getAppointments();
            getOpenRequests();
        }
    }, [settings, agreement]);

    useEffect(() => {
        GetAPIData();
        GetUserDetails();
    }, [selectedAgreement]);

    // Handle account card carousel scroll
    function handleAccountScroll(e) {
        if (!agreement?.accounts) return;
        const container = e.target;
        const firstWrapper = container.firstChild;
        if (!firstWrapper) return;
        // The card is inside a wrapper div, so measure the wrapper
        const cardWidth = firstWrapper.offsetWidth;
        const gap = 12; // matches gap-3
        const index = Math.round(container.scrollLeft / (cardWidth + gap));
        if (index !== activeAccountIndex && agreement.accounts[index]) {
            setActiveAccountIndex(index);
            getTransactionsForAccount(agreement.accounts[index].accountid);
        }
    }

    function getRequestStatusInfo(instance) {
        const status = instance.status;
        if (status === 'AWAITING_ADMIN') return { label: 'Under review', color: '#6B7280', bg: '#F3F4F6' };
        if (status === 'OVERDUE') return { label: 'Overdue', color: '#EF4444', bg: '#FEE2E2' };
        if (status === 'NOT_STARTED' || status === 'IN_PROGRESS') return { label: 'Action required', color: '#D97706', bg: '#FEF3C7' };
        return { label: 'Under review', color: '#6B7280', bg: '#F3F4F6' };
    }

    function getDaysUntil(date) {
        const today = moment().startOf('day');
        const aptDate = moment(date).startOf('day');
        const diff = aptDate.diff(today, 'days');
        if (diff === 0) return 'Today';
        if (diff === 1) return 'Tomorrow';
        return `In ${diff} days`;
    }

    // Skeleton placeholder
    const SkeletonLine = ({ className }) => (
        <div className={`animate-pulse ${themeCSS.skeleton} rounded ${className}`} />
    );

    // Account Balance Card — shared between mobile carousel and desktop arrow nav
    function AccountCard({ account, className, settings: s, agreement: ag, primaryColor: pc, makeAllPayPayment: pay, setAccountModalIsOpen: setModal, setAccountModalFunction: setFunc }) {
        const isArrears = account.direction === 'AR';
        return (
            <div className={`snap-start flex-shrink-0 ${themeCSS.cardBg} rounded-2xl p-5 ${className}`}>

                {/* Card header */}
                <div className="flex items-start justify-between mb-3">
                    <p className={`text-sm font-semibold ${themeCSS.cardTitle}`}>{account.type}</p>
                    <span className={`text-xs ${themeCSS.cardMuted} font-medium`}>#{account.accountreference}</span>
                </div>

                {/* Balance + status — clickable to transactions */}
                <div
                    className="flex items-center justify-between mb-4 cursor-pointer group"
                    onClick={(e) => { e.stopPropagation(); navigate('/transactions', { state: { accountId: account.accountid } }); }}
                >
                    <p className={`text-3xl font-extrabold ${isArrears ? 'text-red-600' : themeCSS.cardTitle}`}>
                        £{account.balance}
                    </p>
                    <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${isArrears
                            ? (theme === 'dark' ? 'bg-red-900/40 text-red-400' : 'bg-red-50 text-red-500')
                            : (theme === 'dark' ? 'bg-green-900/40 text-green-400' : 'bg-green-50 text-green-600')
                        }`}>
                            {isArrears ? 'In arrears' : 'In credit'}
                        </span>
                        <ChevronRight size={18} className={`${themeCSS.cardMuted} group-hover:opacity-75 transition-colors`} />
                    </div>
                </div>

                {/* Action buttons */}
                {(s.payments_enabled === 1 || !!s.directdebit_enabled) && (
                    <div className={`flex items-center border-t ${themeCSS.cardBorder} pt-3 mt-1`}>
                        {s.payments_enabled === 1 && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (paymentLoading) return;
                                    if (s.paymentprovider === 'url') {
                                        window.location.href = s.payment_url;
                                    } else if (s.paymentprovider === 'AllPay') {
                                        if (ag.accounts.length > 1) {
                                            setModal(true);
                                            setFunc('Payment');
                                        } else {
                                            pay(account.accountreference);
                                        }
                                    }
                                }}
                                disabled={paymentLoading}
                                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60"
                                style={{ color: theme === 'highContrast' ? '#000' : pc }}
                            >
                                {paymentLoading ? (
                                    <div className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                                ) : (
                                    <CreditCard size={16} />
                                )}
                                {paymentLoading ? 'Loading…' : 'Make Payment'}
                            </button>
                        )}
                        {!!s.directdebit_enabled && (
                            <>
                                {s.payments_enabled === 1 && <div className={`w-px h-6 ${themeCSS.cardBorder === 'border-black' ? 'bg-black' : theme === 'dark' ? 'bg-slate-600' : 'bg-gray-200'}`} />}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (ag.accounts.length > 1) {
                                            setModal(true);
                                            setFunc('Direct Debit');
                                        } else {
                                            setTriggerAllPayDD(true);
                                        }
                                    }}
                                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                                    style={{ color: theme === 'highContrast' ? '#000' : pc }}
                                >
                                    <Landmark size={16} />
                                    Direct Debit
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: themeCSS.pageBg }}>
            {!loading && userDetails && (
                <HomePageWalkthrough userDetails={userDetails} />
            )}

            {/* Gradient Header */}
            {settings && (
                <div
                    className="w-full relative"
                    style={{
                        background: theme === 'highContrast'
                            ? undefined
                            : `linear-gradient(135deg, ${primaryColor}, ${primaryDark})`,
                    }}
                >
                    {theme === 'highContrast' && (
                        <div className="absolute inset-0 bg-black" />
                    )}
                    <div className="relative z-20 px-4 lg:px-0 pt-4 pb-8 lg:pt-3 lg:pb-3 max-w-5xl mx-auto">
                        {/* Desktop: nav bar with agreement switcher */}
                        <DesktopNavBar
                            unreadCount={unreadCount}
                            agreementId={agreementId}
                            leftSlot={
                                loading ? (
                                    <div className="bg-white/20 backdrop-blur-md rounded-full px-2 py-1.5">
                                        <div className="flex items-center gap-2 rounded-full px-3 py-2">
                                            <HomeIconLucide size={14} className="text-white/30 flex-shrink-0" />
                                            <span className="text-sm font-medium leading-5 text-transparent">
                                                <span className="inline-block animate-pulse bg-white/30 rounded w-64 h-full">&nbsp;</span>
                                            </span>
                                            <ArrowLeftRight size={14} className="text-white/30 flex-shrink-0" />
                                        </div>
                                    </div>
                                ) : agreements && agreements.length > 1 ? (
                                    <div className="bg-white/20 backdrop-blur-md rounded-full px-2 py-1.5">
                                        <button
                                            onClick={() => setIsOpen(true)}
                                            className="flex items-center gap-2 rounded-full px-3 py-2 text-white/90 hover:bg-white/15 transition-colors"
                                            title={agreement?.address || 'Select property'}
                                        >
                                            <HomeIconLucide size={14} className="flex-shrink-0" />
                                            <span className="text-sm font-medium truncate max-w-[400px]">
                                                {agreement?.address || 'Select property'}
                                            </span>
                                            <ArrowLeftRight size={14} className="text-white/70 flex-shrink-0" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="bg-white/20 backdrop-blur-md rounded-full px-2 py-1.5">
                                        <div
                                            className="flex items-center gap-2 rounded-full px-3 py-2 text-white/90"
                                            title={agreement?.address || ''}
                                        >
                                            <HomeIconLucide size={14} className="flex-shrink-0" />
                                            <span className="text-sm font-medium truncate max-w-[400px]">
                                                {agreement?.address || ''}
                                            </span>
                                        </div>
                                    </div>
                                )
                            }
                        />

                        {/* Mobile: property selector + icons row */}
                        <div className="lg:hidden flex items-center justify-between gap-2">
                            {loading ? (
                                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-md rounded-full px-4 py-2.5 min-w-0 flex-1">
                                    <HomeIconLucide size={14} className="text-white/30 flex-shrink-0" />
                                    <span className="text-white text-sm font-medium"><span className="inline-block animate-pulse bg-white/30 rounded w-36">&nbsp;</span></span>
                                    <ArrowLeftRight size={14} className="text-white/30 flex-shrink-0 ml-auto" />
                                </div>
                            ) : agreements && agreements.length > 1 ? (
                                <button
                                    onClick={() => setIsOpen(true)}
                                    className="flex items-center gap-2 bg-white/20 backdrop-blur-md rounded-full px-4 py-2.5 min-w-0 flex-1 hover:bg-white/25 transition-colors"
                                >
                                    <HomeIconLucide size={14} className="text-white/90 flex-shrink-0" />
                                    <span className="text-white text-sm font-medium truncate">
                                        {agreement?.address || 'Select property'}
                                    </span>
                                    <ArrowLeftRight size={14} className="text-white/70 flex-shrink-0 ml-auto" />
                                </button>
                            ) : (
                                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-md rounded-full px-4 py-2.5 min-w-0 flex-1">
                                    <HomeIconLucide size={14} className="text-white/90 flex-shrink-0" />
                                    <span className="text-white text-sm font-medium truncate">
                                        {agreement?.address || ''}
                                    </span>
                                </div>
                            )}
                            <div className="flex items-center bg-white/20 backdrop-blur-md rounded-full px-1 py-1 relative z-50">
                                <div className="relative" ref={headerMenuRef}>
                                    <button
                                        onClick={() => setHeaderMenuOpen(!headerMenuOpen)}
                                        className="p-2 rounded-full hover:bg-white/15 transition-colors"
                                        title="Settings"
                                    >
                                        <Settings size={18} className="text-white/90" />
                                    </button>
                                    {headerMenuOpen && (
                                        <div className={`absolute right-0 mt-2 w-52 ${themeCSS.dropdownBg} rounded-xl shadow-lg ring-1 ${themeCSS.dropdownBorder} py-1 z-50`}>
                                            <p className={`px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>Theme</p>
                                            <button
                                                onClick={() => { updateTheme('light'); setHeaderMenuOpen(false); }}
                                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm ${themeCSS.dropdownText} ${themeCSS.dropdownHover} transition-colors`}
                                            >
                                                <Sun size={16} className="text-amber-500" />
                                                Light
                                                {theme === 'light' && <span className="ml-auto text-xs font-bold" style={{ color: primaryColor }}>Active</span>}
                                            </button>
                                            <button
                                                onClick={() => { updateTheme('dark'); setHeaderMenuOpen(false); }}
                                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm ${themeCSS.dropdownText} ${themeCSS.dropdownHover} transition-colors`}
                                            >
                                                <Moon size={16} className="text-slate-600" />
                                                Dark
                                                {theme === 'dark' && <span className="ml-auto text-xs font-bold" style={{ color: primaryColor }}>Active</span>}
                                            </button>
                                            <button
                                                onClick={() => { updateTheme('highContrast'); setHeaderMenuOpen(false); }}
                                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm ${themeCSS.dropdownText} ${themeCSS.dropdownHover} transition-colors`}
                                            >
                                                <EyeOff size={16} className={theme === 'highContrast' ? 'text-black' : 'text-purple-500'} />
                                                High Contrast
                                                {theme === 'highContrast' && <span className="ml-auto text-xs font-bold" style={{ color: primaryColor }}>Active</span>}
                                            </button>

                                            <div className={`${themeCSS.cardBorder} border-t my-1`} />
                                            <p className={`px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>Text Size</p>
                                            <div className="flex items-center gap-1 px-4 py-2">
                                                {[
                                                    { key: 'normal', label: 'A', size: 'text-sm' },
                                                    { key: 'large', label: 'A', size: 'text-base' },
                                                    { key: 'xl', label: 'A', size: 'text-lg' },
                                                ].map(opt => (
                                                    <button
                                                        key={opt.key}
                                                        onClick={() => updateFontSize(opt.key)}
                                                        className={`flex-1 flex items-center justify-center py-1.5 rounded-lg font-semibold transition-colors ${opt.size} ${
                                                            fontSize === opt.key
                                                                ? 'text-white'
                                                                : `${themeCSS.dropdownText} ${themeCSS.dropdownHover}`
                                                        }`}
                                                        style={fontSize === opt.key ? { backgroundColor: primaryColor } : undefined}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>

                                            <div className={`${themeCSS.cardBorder} border-t my-1`} />
                                            <button
                                                onClick={() => { logout(); }}
                                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                            >
                                                <LogOut size={16} />
                                                Logout
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => navigate('/profile')}
                                    className="p-2 rounded-full hover:bg-white/15 transition-colors"
                                    title="Profile"
                                >
                                    <CircleUserRound size={18} className="text-white/90" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Skeleton account card — shown while loading */}
                    {loading && (
                        <div className="relative z-10 pb-10 lg:pb-14">
                            {/* Mobile skeleton */}
                            <div className="lg:hidden px-8">
                                <div className={`${themeCSS.cardBg} rounded-2xl p-5`} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                    <div className="flex justify-between">
                                        <SkeletonLine className="h-4 w-16" />
                                        <SkeletonLine className="h-4 w-20" />
                                    </div>
                                    <SkeletonLine className="h-8 w-28" />
                                    <SkeletonLine className="h-5 w-20 rounded-full" />
                                    <div className="border-t border-gray-100 pt-3">
                                        <SkeletonLine className="h-8 w-full rounded-xl" />
                                    </div>
                                </div>
                            </div>
                            {/* Desktop skeleton */}
                            <div className="hidden lg:block">
                                <div className="max-w-5xl mx-auto flex gap-8 items-start">
                                    <div className="w-1/3 shrink-0 pt-2">
                                        <div className="animate-pulse bg-white/30 rounded h-10 w-48" />
                                    </div>
                                    <div className="w-2/3">
                                        <div className={`ml-auto ${themeCSS.cardBg} rounded-2xl p-5`} style={{ width: '85%', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                            <div className="flex justify-between">
                                                <SkeletonLine className="h-4 w-16" />
                                                <SkeletonLine className="h-4 w-20" />
                                            </div>
                                            <SkeletonLine className="h-8 w-28" />
                                            <SkeletonLine className="h-5 w-20 rounded-full" />
                                            <div className="border-t border-gray-100 pt-3">
                                                <SkeletonLine className="h-8 w-full rounded-xl" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Account cards — inside the gradient */}
                    {!loading && agreement && agreement.accounts && agreement.accounts.length > 0 && (
                        <div className="relative z-10 pb-10 lg:pb-14">
                            {/* Mobile: swipeable carousel */}
                            <div className="lg:hidden overflow-hidden">
                                <div
                                    ref={carouselRef}
                                    className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                                    onScroll={handleAccountScroll}
                                    style={{ padding: '0 2rem' }}
                                >
                                    {agreement.accounts.map((account, index) => (
                                        <AccountCard
                                            key={account.id || index}
                                            account={account}
                                            className={`snap-center ${agreement.accounts.length === 1 ? 'w-[calc(100vw-2rem)]' : 'w-[calc(100vw-5rem)]'}`}
                                            settings={settings}
                                            agreement={agreement}
                                            primaryColor={primaryColor}
                                            makeAllPayPayment={makeAllPayPayment}
                                            setAccountModalIsOpen={setAccountModalIsOpen}
                                            setAccountModalFunction={setAccountModalFunction}
                                        />
                                    ))}
                                </div>
                                {/* Pagination dots — mobile */}
                                {agreement.accounts.length > 1 && (
                                    <div className="flex justify-center gap-1.5 mt-3">
                                        {agreement.accounts.map((_, index) => (
                                            <div
                                                key={index}
                                                className={`rounded-full transition-all duration-300 ${index === activeAccountIndex
                                                        ? 'w-6 h-2 bg-white'
                                                        : 'w-2 h-2 bg-white/40'
                                                    }`}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Desktop: greeting left, account card right (2/3) */}
                            <div className="hidden lg:block">
                                <div className="max-w-5xl mx-auto flex gap-8 items-start">
                                    {/* Left: greeting (1/3) */}
                                    <div className="w-1/3 shrink-0 pt-2">
                                        <h1 className="text-4xl font-bold text-white leading-tight">
                                            {(() => {
                                                const hour = new Date().getHours();
                                                if (hour < 12) return 'Good morning';
                                                if (hour < 18) return 'Good afternoon';
                                                return 'Good evening';
                                            })()}{userDetails?.firstname ? `, ${userDetails.firstname}` : ''}
                                        </h1>
                                    </div>

                                    {/* Right: account card(s) (2/3) */}
                                    <div className="w-2/3">
                                        {agreement.accounts.length === 1 ? (
                                            /* Single account — align right */
                                            <div className="ml-auto" style={{ width: '85%' }}>
                                                <AccountCard
                                                    account={agreement.accounts[0]}
                                                    className="w-full"
                                                    settings={settings}
                                                    agreement={agreement}
                                                    primaryColor={primaryColor}
                                                    makeAllPayPayment={makeAllPayPayment}
                                                    setAccountModalIsOpen={setAccountModalIsOpen}
                                                    setAccountModalFunction={setAccountModalFunction}
                                                />
                                            </div>
                                        ) : (
                                            /* Multiple accounts — carousel with peek */
                                            <div className="flex items-start gap-3">
                                                {/* Prev button */}
                                                <div className="flex-shrink-0 w-8 pt-12">
                                                    {activeAccountIndex > 0 && (
                                                        <button
                                                            onClick={() => {
                                                                const newIndex = activeAccountIndex - 1;
                                                                setActiveAccountIndex(newIndex);
                                                                getTransactionsForAccount(agreement.accounts[newIndex].accountid);
                                                            }}
                                                            className={`w-8 h-8 rounded-full shadow-md flex items-center justify-center transition-colors ${theme === 'dark' ? 'bg-slate-600 hover:bg-slate-500' : 'bg-white hover:bg-gray-50'}`}
                                                        >
                                                            <ChevronRight size={18} className={`rotate-180 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Cards track */}
                                                <div className="flex-1 min-w-0 overflow-hidden">
                                                    <div
                                                        className="flex gap-3 transition-transform duration-300"
                                                        style={{ transform: `translateX(calc(-${activeAccountIndex} * (85% + 12px)))` }}
                                                    >
                                                        {agreement.accounts.map((account, index) => (
                                                            <div
                                                                key={account.id || index}
                                                                className="flex-shrink-0 transition-opacity duration-300"
                                                                style={{
                                                                    width: '85%',
                                                                    opacity: index === activeAccountIndex ? 1 : 0.5,
                                                                }}
                                                                onClick={() => {
                                                                    if (index !== activeAccountIndex) {
                                                                        setActiveAccountIndex(index);
                                                                        getTransactionsForAccount(account.accountid);
                                                                    }
                                                                }}
                                                            >
                                                                <AccountCard
                                                                    account={account}
                                                                    className={`w-full ${index !== activeAccountIndex ? 'cursor-pointer' : ''}`}
                                                                    settings={settings}
                                                                    agreement={agreement}
                                                                    primaryColor={primaryColor}
                                                                    makeAllPayPayment={makeAllPayPayment}
                                                                    setAccountModalIsOpen={setAccountModalIsOpen}
                                                                    setAccountModalFunction={setAccountModalFunction}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="flex justify-center gap-1.5 mt-3">
                                                        {agreement.accounts.map((_, index) => (
                                                            <button
                                                                key={index}
                                                                onClick={() => {
                                                                    setActiveAccountIndex(index);
                                                                    getTransactionsForAccount(agreement.accounts[index].accountid);
                                                                }}
                                                                className={`rounded-full transition-all duration-300 ${index === activeAccountIndex
                                                                        ? 'w-6 h-2 bg-white'
                                                                        : 'w-2 h-2 bg-white/40 hover:bg-white/60'
                                                                    }`}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Next button */}
                                                <div className="flex-shrink-0 w-8 pt-12">
                                                    {activeAccountIndex < agreement.accounts.length - 1 && (
                                                        <button
                                                            onClick={() => {
                                                                const newIndex = activeAccountIndex + 1;
                                                                setActiveAccountIndex(newIndex);
                                                                getTransactionsForAccount(agreement.accounts[newIndex].accountid);
                                                            }}
                                                            className={`w-8 h-8 rounded-full shadow-md flex items-center justify-center transition-colors ${theme === 'dark' ? 'bg-slate-600 hover:bg-slate-500' : 'bg-white hover:bg-gray-50'}`}
                                                        >
                                                            <ChevronRight size={18} className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Content sheet with rounded top corners — overlaps up into gradient */}
            {settings && agreement && (
                <div className="relative -mt-8">
                    <div className="rounded-t-[2rem] lg:rounded-t-none pt-8 pb-12 px-4" style={{ backgroundColor: themeCSS.pageBg }}>
                        <div className="max-w-5xl mx-auto">
                            <div className="lg:grid lg:grid-cols-5 lg:gap-6">

                                {/* Left column — Transactions */}
                                <div className="lg:col-span-3 space-y-4">

                                {/* Recent Transactions */}
                                {settings.transactions_enabled === 1 && (
                                    <div
                                        className={`${themeCSS.cardBg} rounded-2xl shadow-sm p-5 cursor-pointer hover:shadow-md transition-shadow`}
                                        onClick={() => navigate('/transactions', { state: { accountId: agreement.accounts[activeAccountIndex]?.accountid } })}
                                    >
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className={`text-base font-bold ${themeCSS.cardTitle}`}>Recent Transactions</h3>
                                            <ChevronRight size={18} className={`${themeCSS.cardMuted} flex-shrink-0`} />
                                        </div>
                                        {transactionsLoading ? (
                                            <div className="space-y-4">
                                                {[1, 2].map(i => (
                                                    <div key={i} className="flex items-center gap-3">
                                                        <SkeletonLine className="w-10 h-10 rounded-full" />
                                                        <div className="flex-1 space-y-2">
                                                            <SkeletonLine className="h-4 w-24" />
                                                            <SkeletonLine className="h-3 w-40" />
                                                        </div>
                                                        <SkeletonLine className="h-4 w-16" />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : transactions.length > 0 ? (
                                            <>
                                                {transactions.slice(0, 2).map((txn, index) => {
                                                    const isPayment = txn.type === 'payment';
                                                    return (
                                                        <div
                                                            key={index}
                                                            className={`flex items-center gap-3 py-3 ${index < Math.min(transactions.length - 1, 1)
                                                                    ? `border-b ${themeCSS.cardBorder}`
                                                                    : ''
                                                                }`}
                                                        >
                                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isPayment
                                                                    ? (theme === 'dark' ? 'bg-green-900/40' : 'bg-green-50')
                                                                    : (theme === 'dark' ? 'bg-red-900/40' : 'bg-red-50')
                                                                }`}>
                                                                <CreditCard
                                                                    size={18}
                                                                    className={isPayment ? 'text-green-600' : 'text-red-500'}
                                                                />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className={`text-sm font-semibold ${themeCSS.cardTitle}`}>
                                                                    {txn.type.charAt(0).toUpperCase() + txn.type.slice(1)}
                                                                </p>
                                                                <p className={`text-xs ${themeCSS.cardSubtext} truncate`}>
                                                                    {txn.description}
                                                                </p>
                                                            </div>
                                                            <div className="text-right flex-shrink-0">
                                                                <p className={`text-sm font-semibold ${isPayment ? 'text-green-600' : themeCSS.cardTitle
                                                                    }`}>
                                                                    £{txn.value}
                                                                </p>
                                                                <p className={`text-xs ${themeCSS.cardMuted}`}>
                                                                    {moment(txn.date).format('D MMM')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        ) : (
                                            <div className={`flex flex-col items-center py-6 ${themeCSS.cardMuted}`}>
                                                <CreditCard size={32} className="mb-2" />
                                                <p className="text-sm">No recent transactions</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Right column — Appointments + Ongoing */}
                            <div className="lg:col-span-2 space-y-4 mt-4 lg:mt-0">
                                {/* Appointments */}
                                {settings.repairs_enabled === 1 && (
                                    <div
                                        className={`${themeCSS.cardBg} rounded-2xl shadow-sm p-5 cursor-pointer hover:shadow-md transition-shadow`}
                                        onClick={() => window.location.href = '/appointments'}
                                    >
                                        <h3 className={`text-base font-bold ${themeCSS.cardTitle} mb-3`}>Appointments</h3>
                                        {appointments.length > 0 ? (
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getDaysUntil(appointments[0].appointmentdate) === 'Today'
                                                        ? (theme === 'dark' ? 'bg-red-900/40' : 'bg-red-50')
                                                        : (theme === 'dark' ? 'bg-indigo-900/40' : 'bg-indigo-50')
                                                    }`}>
                                                    <CalendarDays
                                                        size={18}
                                                        className={getDaysUntil(appointments[0].appointmentdate) === 'Today'
                                                            ? 'text-red-500'
                                                            : 'text-indigo-500'
                                                        }
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm font-medium ${themeCSS.cardTitle}`}>
                                                        {appointments[0].starttime} - {appointments[0].endtime} · {moment(appointments[0].appointmentdate).format('D MMM')}
                                                    </p>
                                                    <p className={`text-xs ${themeCSS.cardSubtext}`}>
                                                        {getDaysUntil(appointments[0].appointmentdate)}
                                                        {appointments.length > 1 ? ` · ${appointments.length - 1} more` : ''}
                                                    </p>
                                                </div>
                                                <ChevronRight size={18} className={`${themeCSS.cardMuted} flex-shrink-0`} />
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${themeCSS.badgeMuted}`}>
                                                    <CalendarDays size={18} />
                                                </div>
                                                <p className={`flex-1 text-sm ${themeCSS.cardSubtext}`}>No appointments scheduled</p>
                                                <ChevronRight size={18} className={`${themeCSS.cardMuted} flex-shrink-0`} />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Ongoing (Repairs + Requests) */}
                                <div className={`${themeCSS.cardBg} rounded-2xl shadow-sm p-5`}>
                                    <h3 className={`text-base font-bold ${themeCSS.cardTitle} mb-3`}>Ongoing</h3>
                                    {openRepairs.length === 0 && openRequests.length === 0 ? (
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${themeCSS.badgeMuted}`}>
                                                <ClipboardCheck size={18} />
                                            </div>
                                            <p className={`flex-1 text-sm ${themeCSS.cardSubtext}`}>Nothing ongoing</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-1">
                                            {/* Open Repairs */}
                                            {openRepairs.slice(0, 3).map((repair, index) => (
                                                <div
                                                    key={`repair-${repair.repairid}-${index}`}
                                                    className={`flex items-center gap-3 py-3 cursor-pointer ${themeCSS.cardHover} -mx-2 px-2 rounded-lg transition-colors`}
                                                    onClick={() => navigate('/repairs')}
                                                >
                                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${theme === 'dark' ? 'bg-sky-900/40' : 'bg-sky-50'}`}>
                                                        <Hammer size={18} className="text-sky-500" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className={`text-sm font-medium ${themeCSS.cardTitle} truncate`}>
                                                            {repair.description}
                                                        </p>
                                                        <p className={`text-xs ${themeCSS.cardSubtext}`}>
                                                            Repair #{repair.repairid} · Reported {moment(repair.date).format('D MMM')}
                                                        </p>
                                                    </div>
                                                    <ChevronRight size={18} className={`${themeCSS.cardMuted} flex-shrink-0`} />
                                                </div>
                                            ))}

                                            {/* Open Requests */}
                                            {openRequests.slice(0, 3).map((request) => {
                                                const statusInfo = getRequestStatusInfo(request);
                                                return (
                                                    <div
                                                        key={`request-${request.id}`}
                                                        className={`flex items-center gap-3 py-3 cursor-pointer ${themeCSS.cardHover} -mx-2 px-2 rounded-lg transition-colors`}
                                                        onClick={() => navigate('/requests', { state: { openInstanceId: request.id } })}
                                                    >
                                                        <div
                                                            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                                            style={{
                                                                backgroundColor: statusInfo.label === 'Action required' ? '#FEF3C7' : '#F3E8FF'
                                                            }}
                                                        >
                                                            <ClipboardCheck
                                                                size={18}
                                                                className={statusInfo.label === 'Action required' ? 'text-amber-600' : 'text-purple-600'}
                                                            />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <p className={`text-sm font-medium ${themeCSS.cardTitle} truncate`}>
                                                                    {request.task_flow_name}
                                                                </p>
                                                                <span
                                                                    className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                                                                    style={{ backgroundColor: statusInfo.bg, color: statusInfo.color }}
                                                                >
                                                                    {statusInfo.label}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                <p className={`text-xs ${themeCSS.cardSubtext}`}>
                                                                    {request.due_date ? `Due ${moment(request.due_date).format('D MMM')}` : 'Request'}
                                                                </p>
                                                                {request.unread_message_count > 0 && (
                                                                    <span
                                                                        className="flex items-center gap-1 text-[10px] font-bold text-white px-1.5 py-0.5 rounded-md"
                                                                        style={{ backgroundColor: primaryColor }}
                                                                    >
                                                                        <MessageSquare size={10} />
                                                                        {request.unread_message_count}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <ChevronRight size={18} className={`${themeCSS.cardMuted} flex-shrink-0`} />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )
                                    }
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
                </div>
            )}

            {/* Loading state — content section skeletons */}
            {loading && settings && (
                <div className="relative -mt-8">
                    <div className="rounded-t-[2rem] lg:rounded-t-none pt-8 pb-12 px-4" style={{ backgroundColor: themeCSS.pageBg }}>
                        <div className="max-w-5xl mx-auto">
                            <div className="lg:grid lg:grid-cols-5 lg:gap-6">
                                <div className="lg:col-span-3 space-y-4">
                                    <div className={`${themeCSS.cardBg} rounded-2xl shadow-sm p-5 space-y-4`}>
                                        <SkeletonLine className="h-5 w-40" />
                                        <div className="space-y-4">
                                            {[1, 2].map(i => (
                                                <div key={i} className="flex items-center gap-3">
                                                    <SkeletonLine className="w-10 h-10 rounded-full" />
                                                    <div className="flex-1 space-y-2">
                                                        <SkeletonLine className="h-4 w-24" />
                                                        <SkeletonLine className="h-3 w-40" />
                                                    </div>
                                                    <SkeletonLine className="h-4 w-16" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="lg:col-span-2 space-y-4 mt-4 lg:mt-0">
                                    {[1, 2].map(i => (
                                        <div key={i} className={`${themeCSS.cardBg} rounded-2xl shadow-sm p-5 space-y-3`}>
                                            <SkeletonLine className="h-5 w-32" />
                                            <div className="flex items-center gap-3">
                                                <SkeletonLine className="w-10 h-10 rounded-full" />
                                                <div className="flex-1 space-y-2">
                                                    <SkeletonLine className="h-4 w-full" />
                                                    <SkeletonLine className="h-3 w-3/4" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Footer />

            {/* Agreement Switcher Modal */}
            <AgreementSwitcher modalState={modalIsOpen} setModalState={setIsOpen} titleText={'Please select an agreement'}>
                {agreements && (
                    agreements.map(function (ag, index) {
                        return (
                            <div
                                onClick={() => { switchAccount(ag.reference) }}
                                key={ag.id}
                                value={ag}
                                className="flex cursor-default select-none rounded-xl p-3 hover:bg-gray-50 transition-colors"
                            >
                                {ag.type === "Residential" ? (
                                    <div className='flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-indigo-500'>
                                        <HomeIcon className='h-6 w-6 flex-none text-white' />
                                    </div>
                                ) : (
                                    <div className='flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-teal-500'>
                                        <DocumentTextIcon className='h-6 w-6 flex-none text-white' />
                                    </div>
                                )}
                                <div className="ml-4 flex-auto">
                                    <p className='text-sm font-medium'>
                                        {ag.address}
                                    </p>
                                    {ag.type === "Expired" && (
                                        <span className="text-red-500 font-bold">Former Account </span>
                                    )}
                                    Agreement Reference: {ag.reference}
                                </div>
                            </div>
                        )
                    })
                )}
            </AgreementSwitcher>

            {/* Account Switcher Modal */}
            <AccountSwitcher modalState={accountModalIsOpen} setModalState={setAccountModalIsOpen} titleText={'Please select an account'}>
                {agreement && agreement.accounts && agreement.accounts.map((account) => (
                    <div
                        onClick={() => {
                            setSelectedAccount(account);
                            if (settings.paymentprovider === 'AllPay' && accountModalFunction === 'Payment') {
                                makeAllPayPayment(account.accountreference);
                            } else if (settings.paymentprovider === 'AllPay' && accountModalFunction === 'Direct Debit') {
                                setTriggerAllPayDD(true);
                            }
                        }}
                        key={account.id}
                        value={account}
                        className='flex cursor-default select-none rounded-xl p-3 hover:bg-gray-100'
                    >
                        <div className="flex-auto">
                            <p className='text-sm font-medium text-gray-900'>
                                {account.type} <span>(Account Reference: {account.accountreference})</span>
                            </p>
                            <p className='text-sm text-gray-700'>
                                {agreement.address}
                            </p>
                        </div>
                        {account.direction === 'CR' ? (
                            <div className='flex h-10 flex-none items-center justify-center rounded-lg bg-green-600 w-28'>
                                <p className='text-white text-lg'>£{account.balance}</p>
                            </div>
                        ) : (
                            <div className='flex h-10 flex-none items-center justify-center rounded-lg bg-red-400 w-28'>
                                <p className='text-white text-lg'>£{account.balance}</p>
                            </div>
                        )}
                    </div>
                ))}
            </AccountSwitcher>

            {/* Hidden AllPay Direct Debit form */}
            {settings && !!settings.directdebit_enabled && selectedAccount && (
                <div className="hidden">
                    <AllPayDirectDebitCard
                        agreement={agreement}
                        accountreference={selectedAccount.accountreference}
                        modalState={setAccountModalIsOpen}
                        accountModalFunction={setAccountModalFunction}
                        shouldTriggerDDSetup={triggerAllPayDD}
                    />
                </div>
            )}

            {/* AllPay Payment Modal */}
            {paymentModal && (
                <>
                    {/* Backdrop */}
                    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={!paymentResult ? undefined : closePaymentModal} />

                    {/* Single modal container — bottom sheet on mobile, centered on desktop */}
                    <div className="fixed inset-0 z-50 flex items-end lg:items-center lg:justify-center lg:p-4">
                        <div className={`w-full animate-slideUp flex flex-col rounded-t-2xl lg:rounded-2xl shadow-2xl lg:max-w-lg overflow-hidden ${
                            theme === 'dark' ? 'bg-slate-800' : theme === 'highContrast' ? 'bg-white' : 'bg-gray-50'
                        }`} style={{ height: '92vh', maxHeight: '92vh' }}>
                            {/* Drag handle — mobile only */}
                            <div className="flex justify-center pt-3 pb-1 flex-shrink-0 lg:hidden">
                                <div className={`w-10 h-1 rounded-full ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                            </div>
                            {/* Header */}
                            <div className="flex items-center justify-between px-5 pb-3 lg:pt-5 flex-shrink-0">
                                <h2 className={`text-lg font-bold ${themeCSS.cardTitle}`}>Make Payment</h2>
                                {/* Mobile: text Done button */}
                                <button onClick={closePaymentModal} className="text-sm font-semibold lg:hidden" style={{ color: primaryColor }}>Done</button>
                                {/* Desktop: X button */}
                                <button
                                    onClick={closePaymentModal}
                                    className={`hidden lg:flex w-8 h-8 rounded-full items-center justify-center transition-colors ${theme === 'dark' ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`}
                                >
                                    <X size={18} className={themeCSS.cardMuted} strokeWidth={2} />
                                </button>
                            </div>
                            {/* Content */}
                            <div className="flex-1 min-h-0">
                                {paymentResult ? (
                                    <div className="flex flex-col items-center justify-center h-full px-5">
                                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${paymentResult === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
                                            {paymentResult === 'success'
                                                ? <CreditCard size={28} className="text-green-600" />
                                                : <X size={28} className="text-red-500" />
                                            }
                                        </div>
                                        <p className={`text-lg font-bold mb-2 ${paymentResult === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                                            {paymentResult === 'success' ? 'Payment Successful' : paymentResult === 'cancelled' ? 'Payment Cancelled' : 'Payment Failed'}
                                        </p>
                                        {paymentResult === 'success' && (
                                            <p className={`text-sm text-center ${themeCSS.cardMuted}`}>
                                                It can take up to one week for the payment to appear on your account.
                                            </p>
                                        )}
                                        <button
                                            onClick={closePaymentModal}
                                            className="mt-6 px-8 py-3 rounded-xl text-sm font-semibold text-white"
                                            style={{ backgroundColor: primaryColor }}
                                        >
                                            Done
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex flex-col h-full">
                                        <iframe
                                            name="PaymentModalIFrame"
                                            id="PaymentModalIFrame"
                                            title="Make Payment"
                                            className="w-full flex-1 border-0"
                                        />
                                        <p className={`text-xs text-center py-2.5 flex-shrink-0 border-t ${theme === 'dark' ? 'border-slate-700' : 'border-gray-100'} ${themeCSS.cardMuted}`}>Please do not refresh the page while your payment is being processed</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
